import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authService } from '../services/authService'

interface User {
  id: number
  username: string
  email: string
  firstName: string
  lastName: string
  avatar?: string
  role: string
  permissions: string[]
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  isInitializing: boolean

  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refreshAccessToken: () => Promise<boolean>
  checkAuth: () => Promise<boolean>
  updateUser: (user: Partial<User>) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      isInitializing: true,

      login: async (username: string, password: string) => {
        set({ isLoading: true })
        try {
          const response = await authService.login(username, password)
          const { user, tokens } = response.data.data
          set({
            user,
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            isAuthenticated: true,
            isLoading: false,
          })
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },

      logout: async () => {
        const { refreshToken } = get()
        if (refreshToken) {
          try {
            await authService.logout(refreshToken)
          } catch {
            // Ignore logout errors
          }
        }

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        })
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get()
        if (!refreshToken) return false

        try {
          const response = await authService.refresh(refreshToken)
          set({ 
            accessToken: response.data.data.accessToken,
            isAuthenticated: true,
          })
          return true
        } catch {
          // Clear auth state on refresh failure
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
          })
          window.location.href = '/login'
          return false
        }
      },

      checkAuth: async () => {
        set({ isInitializing: true })
        const { accessToken, refreshToken, refreshAccessToken } = get()

        // If no tokens at all, user is not authenticated
        if (!accessToken && !refreshToken) {
          set({ 
            isAuthenticated: false, 
            isInitializing: false,
            user: null,
          })
          return false
        }

        // If we have an access token, try to validate it
        if (accessToken) {
          try {
            const response = await authService.getCurrentUser()
            set({
              user: response.data.data,
              isAuthenticated: true,
              isInitializing: false,
            })
            return true
          } catch {
            // Access token invalid, try to refresh
          }
        }

        // Try to refresh the token
        const refreshed = await refreshAccessToken()
        if (refreshed) {
          try {
            const response = await authService.getCurrentUser()
            set({
              user: response.data.data,
              isAuthenticated: true,
              isInitializing: false,
            })
            return true
          } catch {
            // Failed to get user even after refresh
          }
        }

        // All attempts failed
        set({ 
          isAuthenticated: false, 
          isInitializing: false,
          user: null,
          accessToken: null,
          refreshToken: null,
        })
        return false
      },

      updateUser: (userData) => {
        const { user } = get()
        if (user) {
          set({ user: { ...user, ...userData } })
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
